import type { CompetitorPost } from "@/lib/publications/types";
import { ArrowRight, Clock } from "lucide-react";
import Link from "next/link";
import { formatUpdatedDate } from "../_logic/format";
import VsLockup from "./VsLockup";

interface ComparisonCardProps {
  competitor: CompetitorPost;
  href: string;
}

/**
 * Link card for one comparison: logo lockup on a tinted panel, then title, summary and meta.
 * @param props - the comparison post and its href
 * @returns the card
 */
export default function ComparisonCard({ competitor, href }: ComparisonCardProps) {
  return (
    <Link
      href={href}
      className="group flex flex-col gap-1 rounded-3xl border border-landing-primary bg-white p-2 shadow-[0_1px_3px_0_oklch(0.4_0.02_280/0.05)] transition-[box-shadow,border-color] duration-200 hover:border-[#4f474033] hover:shadow-[0_2px_8px_-1px_oklch(0.4_0.03_280/0.08)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--landing-cta-light)]"
    >
      <div className="flex aspect-[2/1] items-center justify-center rounded-[18px] bg-landing-secondary">
        <div className="transition-transform duration-300 ease-out group-hover:scale-[1.03] motion-reduce:transition-none">
          <VsLockup competitorLogo={competitor.competitorLogo} size="md" />
        </div>
      </div>
      <div className="flex flex-1 flex-col gap-2 p-4">
        <h3 className="text-tag-lg text-landing-primary">{competitor.title}</h3>
        {competitor.metaDescription && (
          <p className="line-clamp-2 text-body-sm text-landing-secondary">{competitor.metaDescription}</p>
        )}
        <div className="mt-auto flex items-center justify-between gap-3 pt-2 text-body-sm text-landing-secondary">
          <span className="flex items-center gap-1.5">
            <Clock className="size-3.5" aria-hidden />
            {competitor.readTime} min read · Updated {formatUpdatedDate(competitor.updatedAt, "short")}
          </span>
          <ArrowRight
            className="size-4 shrink-0 text-landing-primary transition-transform duration-200 group-hover:translate-x-0.5"
            aria-hidden
          />
        </div>
      </div>
    </Link>
  );
}
